import { FC, useCallback, useMemo, useRef } from 'react';
import TableGridItem from '../../UI/TableGridItem';
import { createColumnDefs, defaultColumnDefs, WorkingHour } from '../helpers/utils';
import { percentageCellStyleRenderer } from '../../../utils/agGridUtils';
import { createWorkingHoursRowFilter } from '../../../utils/workingHoursUtils';
import type { Dayjs } from '@utils/dayjs';

interface PercentagesTabPanelProps {
    allDatesInAMonth: Dayjs[];
    rowData: { [k: string]: string | unknown }[];
    isLoading?: boolean;
    hideNonWorkingTime?: boolean;
    workingHours?: WorkingHour[];
}

const PercentagesTabPanel: FC<PercentagesTabPanelProps> = ({
    allDatesInAMonth,
    rowData,
    isLoading,
    hideNonWorkingTime = true,
    workingHours,
}) => {
    const gridRef = useRef(null);

    // Memoize cell style renderer for performance
    const cellStyleRenderer = useCallback(
        (params: any) =>
            percentageCellStyleRenderer({
                value: params.value,
                maxValue: 100,
            }),
        []
    );

    // Memoize column definitions to avoid re-creation on every render
    const columnDefs = useMemo(
        () =>
            createColumnDefs({
                allDatesInAMonth,
                valuePropertyName: 'percentage',
                showPercentageSign: true,
                cellStyleRenderer,
            }),
        [allDatesInAMonth, cellStyleRenderer]
    );

    // Filter rows to hide non-working time slots if toggle is enabled
    const filteredRowData = useMemo(() => {
        const filterFn = createWorkingHoursRowFilter(allDatesInAMonth, workingHours, hideNonWorkingTime);
        return rowData.filter(filterFn);
    }, [rowData, hideNonWorkingTime, allDatesInAMonth, workingHours]);

    return (
        <TableGridItem
            ref={gridRef}
            rowData={filteredRowData}
            defaultColDef={defaultColumnDefs}
            columnDefs={columnDefs}
            columnHoverHighlight={true}
            loading={isLoading}
        />
    );
};

export default PercentagesTabPanel;
